<script setup lang="ts">
import { ref } from "vue"
import Section from "@/types/static"

// data
const intro =
  'XYPN ("us", "we", or "our") operates http://archive.xyplanningnetwork.com website (hereinafter referred to as the "XY Archive Service"). This page informs you of our policies regarding the collection, use and disclosure of personal data when you use our XY Archive Service and the choices you have associated with that data. We use your data to provide and improve the XY Archive Service. By using the XY Archive Service, you agree to the collection and use of information in accordance with this policy.'
const sections = ref<Section[]>([
  {
    open: false,
    title: "Definitions",
    paragraphs: [
      {
        subheader: "Service",
        content:
          "XY Archive Service is the http://archive.xyplanningnetwork.com website operated by XYPN.",
      },
      {
        subheader: "Personal Data",
        content:
          "Personal Data means data about a living individual who can be identified from those data (or from those and other information either in our possession or likely to come into our possession).",
      },
      {
        subheader: "Usage Data",
        content:
          "Usage Data is data collected automatically either generated by the use of the Service or from the XY Archive Service infrastructure itself (for example, the duration of a page visit).",
      },
      {
        subheader: "Cookies",
        content:
          "Cookies are small files stored on your device (computer or mobile device).",
      },
    ],
  },
  {
    open: false,
    title: "Information Collection and Use",
    paragraphs: [
      {
        content:
          "We collect several different types of information for various purposes to provide and improve our XY Archive Service to you.",
      },
      {
        header: "Types of Data Collected",
        subheader: "Personal Data",
        content:
          'While using our XY Archive Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you ("Personal Data"). Personally identifiable information may include, but is not limited to:',
        list: [
          "Email address",
          "First name and last name",
          "Cookies and Usage Data",
        ],
      },
      {
        content:
          "In addition, the XY Archive Service collects your email, website, and various social media channel data. All of this collection is set up, and removable, by you. Each separate source will require your permission to begin collection of this data.",
      },
      {
        subheader: "Google User Data",
        content:
          "If you set up a Google gmail archiving source, we will gain the ability to access your gmail emails with a read-only ability. We will store this data at AWS. We will never share your Google user data with anyone other than you.",
      },
      {
        content: "By using any Google service, including",
        list: [
          "Google authentication for login",
          "Google email archiving",
          "YouTube archiving",
        ],
      },
      {
        content:
          "You are agreeing to be bound by Google’s Privacy Policy. Their Privacy Policy can be found here: https://policies.google.com/privacy. If you ever want to change your security settings with Google, you can manage those settings here: https://myaccount.google.com/permissions.",
      },
      {
        content:
          "Our collection, use, and transfer to any other app of information received from Google Accounts and Google user data adheres to Google API Services User Data Policy (https://developers.google.com/terms/api-services-user-data-policy), including their Limited Use Requirements.",
        list: [
          "Allowed Use. Our developers use a restricted scope to access your gmail emails. We will access your email once per day to create a record of any sent or received emails. This is done solely to provide the core XY Archive Service of compliantly archiving your data.",
          "Allowed Transfer. We will only transfer your data to others if (a) necessary to provide or improve user-facing features that are prominent from the requesting app's user interface, (b) to comply with applicable laws, or (c) a part of a merger, acquisition or sale of assets of the developer.",
          "Prohibited Advertising. We will never use any data obtained from Google to serve you advertisements.",
          "Prohibited Human Interaction. No human, outside of you, will ever read your accessed user email data. No employee at XYPN will read your emails obtained through archiving. The only exceptions are if a) you grant us limited permission to do so to provide support, b) reading is necessary for security purposes, c) we need to do so to comply with laws, and d) if we anonymize and aggregate all user data for internal operations.",
        ],
      },
      {
        content:
          "Additional Limits on Use of Your Google User Data: Notwithstanding anything else in this Privacy Policy, if you provide the App access to the following types of your Google data, the App's use of that data will be subject to these additional restrictions:",
        list: [
          "The App will only use access to read, write, modify, or control Gmail message bodies (including attachments), metadata, headers, and settings to provide a web email client that allows users to compose, send, read, and process emails and will not transfer this Gmail data to others unless doing so is necessary to provide and improve these features, comply with applicable law, or as part of a merger, acquisition, or sale of assets.",
          "The App will not use this Gmail data for serving advertisements.",
          "The App will not allow humans to read this data unless we have your affirmative agreement for specific messages, doing so is necessary for security purposes such as investigating abuse, to comply with applicable law, or for the App's internal operations and even then only when the data have been aggregated and anonymized.",
        ],
      },
      {
        subheader: "YouTube Data",
        content:
          "We use YouTube API services in order to archive your YouTube data. If you choose to utilize our YouTube archiving services, you are agreeing to be bound by YouTube’s Terms of Service. Their Terms of Service can be found here: https://www.youtube.com/t/terms.",
      },
      {
        subheader: "Usage Data",
        content:
          'We may also collect information on how the XY Archive Service is accessed and used ("Usage Data"). This Usage Data may include information such as your computer\'s Internet Protocol address (e.g. IP address), browser type, browser version, the pages of our XY Archive Service that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data.',
      },
      {
        subheader: "Tracking Cookies Data",
        content:
          "We use cookies and similar tracking technologies to track the activity on our XY Archive Service and we hold certain information.",
      },
      {
        content:
          "Cookies are files with a small amount of data which may include an anonymous unique identifier. Cookies are sent to your browser from a website and stored on your device. Other tracking technologies are also used such as beacons, tags and scripts to collect and track information and to improve and analyze our XY Archive Service.",
      },
      {
        content:
          "You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our XY Archive Service.",
      },
      {
        content: "Examples of Cookies we use:",
        list: [
          "Session Cookies. We use Session Cookies to operate our XY Archive Service.",
          "Preference Cookies. We use Preference Cookies to remember your preferences and various settings.",
          "Security Cookies. We use Security Cookies for security purposes.",
        ],
      },
      {
        content: "XYPN uses the collected data for various purposes:",
        list: [
          "To provide and maintain our XY Archive Service",
          "To notify you about changes to our XY Archive Service",
          "To allow you to participate in interactive features of our XY Archive Service when you choose to do so",
          "To provide customer support",
          "To gather analysis or valuable information so that we can improve our XY Archive Service",
          "To monitor the usage of our XY Archive Service",
          "To detect, prevent and address technical issues",
          "To provide you with news, special offers and general information about other goods, services and events which we offer that are similar to those that you have already purchased or inquired about unless you have opted not to receive such information.",
        ],
      },
    ],
  },
  {
    open: false,
    title: "Transfer of Data",
    paragraphs: [
      {
        content:
          "Your information, including Personal Data, may be transferred to - and maintained on - computers located outside of your state, province, country or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.",
      },
      {
        content:
          "If you are located outside the United States and choose to provide information to us, please note that we transfer the data, including Personal Data, to the United States and process it there.",
      },
      {
        content:
          "Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.",
      },
      {
        content:
          "XYPN will take all steps reasonably necessary to ensure that your data is treated securely and in accordance with this Privacy Policy and no transfer of your Personal Data will take place to an organization or a country unless there are adequate controls in place including the security of your data and other personal information.",
      },
    ],
  },
  {
    open: false,
    title: "Disclosure of Data",
    paragraphs: [
      {
        header: "Disclosure for Law Enforcement",
        content:
          "Under certain circumstances, XYPN may be required to disclose your Personal Data if required to do so by law or in response to valid requests by public authorities (e.g. a court or a government agency).",
      },
      {
        header: "Legal Requirements",
        content:
          "XYPN may disclose your Personal Data in the good faith belief that such action is necessary to:",
        list: [
          "To comply with a legal obligation",
          "To protect and defend the rights or property of XYPN",
          "To prevent or investigate possible wrongdoing in connection with the XY Archive Service",
          "To protect the personal safety of users of the XY Archive Service or the public",
          "To protect against legal liability",
        ],
      },
      {
        subheader: "Lawful Basis for Processing Data",
        content:
          "In certain jurisdictions, the processing of your Personal Data (as defined below) is lawful only if it is permitted under data protection legislation. In this case, we have a lawful basis for each of our processing activities (unless an exemption applies) as outlined below:",
      },
      {
        content:
          "Consent. By using the Site, the Software, or otherwise submitting your Personal Data to us, you consent to our collection, use and sharing of your information as described in this Privacy Policy.",
      },
      {
        content:
          "Legitimate Interest. We will process your Personal Data based on Legitimate Interest, and particularly where it is used for marketing and business purposes, and to contact you to start a conversation regarding your use of the Site or the Software.",
      },
      {
        content:
          "Contract. We may process your Personal Data where such processing is required in order to fulfill an agreement or contract with you.",
      },
      {
        content:
          "Legal Obligation. We may also process your Personal Data based on other lawful basis, such as where the processing is necessary to comply with applicable law.",
      },
      {
        subheader: "Security of Data",
        content:
          "The security of your data is important to us but remember that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your Personal Data, we cannot guarantee its absolute security.",
      },
    ],
  },
  {
    open: false,
    title: "Deletion of Data",
    paragraphs: [
      {
        content: "Please contact us to request the deletion of your data.",
      },
    ],
  },
  {
    open: false,
    title:
      'Our Policy on "Do Not Track" Signals under the California Online Protection Act (CalOPPA)',
    paragraphs: [
      {
        content:
          'We do not support Do Not Track ("DNT"). Do Not Track is a preference you can set in your web browser to inform websites that you do not want to be tracked.',
      },
      {
        content:
          "You can enable or disable Do Not Track by visiting the Preferences or Settings page of your web browser.",
      },
    ],
  },
  {
    open: false,
    title: "Service Providers",
    paragraphs: [
      {
        content:
          'We may employ third party companies and individuals to facilitate our XY Archive Service ("Service Providers"), provide the XY ArchiveService on our behalf, perform XY Archive Service-related services or assist us in analysing how our XY Archive Service is used.',
      },
      {
        content:
          "These third parties have access to your Personal Data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.",
      },
      {
        content:
          "The XY Archive Site and the Software may contain links to other third-party owned and/or operated websites and, where you request information from third-party providers, we may transfer you directly to the websites of such third-party providers through the use of an iFrame. XYPN is not responsible for the privacy practices or the content of such third-party websites. In some cases, you may be required to provide certain information to register or complete a transaction at such website. These third-party websites have separate privacy and data collection practices and XYPN has no responsibility or liability relating to them. We do not endorse, nor are we responsible for the accuracy of, the privacy policies and/or terms and conditions of such third-party website. These third-party entities are independent third parties and are not affiliated with XYPN.",
      },
      {
        content:
          "Third-party Technology/Software/Service/Solutions Providers. The XY Archive Site or the Software may contain certain software technologies and services licensed from third-party providers. XYPN may engage with certain third-party services and solutions providers such as data hosting service providers, payment processing service providers, and email marketing service providers. These third-party providers have separate privacy and data collection practices and policies, and XYPN has no responsibility or liability relating to them. We do not endorse, nor are we responsible for the accuracy of the terms and conditions of any third-party privacy policies. These third-party entities are independent third parties and are not affiliated with XYPN.",
      },
      {
        content:
          "XYPN may be using or sharing your Personal Data for Legitimate Interest purposes with third party service providers, including:",
        list: [
          "Facebook (Advertising Services); https://www.facebook.com/privacy/explanation",
          "Facebook Custom Audience (Remarketing and Behavioral Targeting); https://www.facebook.com/about/privacy/explanation",
          "Google Analytics (Traffic/Campaign Monitoring Services); https://policies.google.com/privacy",
          "Google Adwords (Advertising Services); https://policies.google.com/privacy",
          "Google Similar Audiences for Search (Remarketing and Behavioral Targeting); https://policies.google.com/privacy",
          "AppCues (in-app user engagement platform); https://www.appcues.com/privacy",
          "Ruby Receptionist (Telecommunications); https://www.callruby.com/privacy-policy/",
          "Notion (Task management and workflow optimization service); https://www.notion.so/Terms-and-Privacy-28ffdd083dc3473e9c2da6ec011b58ac",
          "HelpScout (HelpDesk Software); https://www.helpscout.com/company/legal/privacy/",
          "Slack (Internal team communication) We have set up channels that automatically notify us of the HelpScout messages, therefore some user personal information will be displayed; https://slack.com/privacy-policy",
          "HubSpot (Marketing); https://legal.hubspot.com/privacy-policy",
          "Retently (Recurring Marketing surveys); https://www.retently.com/privacy-policy/",
        ],
      },
    ],
  },
  {
    open: false,
    title: "Links to Other Sites",
    paragraphs: [
      {
        content:
          "Our XY Archive Service may contain links to other sites that are not operated by us. If you click a third party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit.",
      },
      {
        content:
          "We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.",
      },
    ],
  },
  {
    open: false,
    title: "Children's Privacy",
    paragraphs: [
      {
        content:
          'Our XY Archive Service does not address anyone under the age of 18 ("Children").',
      },
      {
        content:
          "We do not knowingly collect personally identifiable information from anyone under the age of 18. If you are a parent or guardian and you are aware that your Child has provided us with Personal Data, please contact us. If we become aware that we have collected Personal Data from children without verification of parental consent, we take steps to remove that information from our servers.",
      },
    ],
  },
  {
    open: false,
    title: "Changes to This Privacy Policy",
    paragraphs: [
      {
        content:
          "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page.",
      },
      {
        content:
          'We will let you know via email and/or a prominent notice on our XY Archive Service, prior to the change becoming effective and update the "effective date" at the top of this Privacy Policy.',
      },
      {
        content:
          "You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.",
      },
    ],
  },
])
</script>

<template>
  <div class="bg-gray-50">
    <div class="max-w-screen-xl mx-auto py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
      <div class="max-w-3xl mx-auto">
        <h2
          class="text-center text-3xl leading-9 font-extrabold text-gray-900 sm:text-4xl sm:leading-10"
        >
          Privacy Policy
        </h2>
        <h3
          class="mt-1 text-center text-lg leading-4 text-gray-600 sm:text-xl sm:leading-5"
        >
          XY Archive Privacy Policy<br />
          Effective date: January 19, 2021
        </h3>
        <p class="text-sm leading-6 text-gray-600 mt-6" v-text="intro"></p>
        <div class="mt-6 border-t-2 border-gray-200 pt-6">
          <dl>
            <div
              v-for="(section, idx) in sections"
              :key="idx"
              :class="{ 'mt-6 border-t border-gray-200 pt-6': idx > 0 }"
            >
              <dt class="text-lg leading-7">
                <button
                  class="text-left w-full flex justify-between items-start text-gray-400 focus:outline-none focus:text-gray-900"
                  @click="section.open = !section.open"
                >
                  <span class="font-medium text-gray-900">
                    {{ section.title }}
                  </span>
                  <span class="ml-6 h-7 flex items-center">
                    <svg
                      class="h-6 w-6 transform"
                      :class="{ '-rotate-180': section.open }"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </button>
              </dt>
              <dd
                v-for="(paragraph, pidx) in section.paragraphs"
                v-show="section.open"
                :key="idx - pidx"
                class="mt-2 pr-12"
              >
                <h3
                  v-if="!!paragraph.header"
                  class="my-4 font-semibold"
                  v-text="paragraph.header"
                ></h3>
                <h4
                  v-if="!!paragraph.subheader"
                  class="text-gray-600"
                  v-text="paragraph.subheader"
                ></h4>
                <p
                  class="text-base leading-6 text-gray-600"
                  v-text="paragraph.content"
                ></p>
                <ol v-if="!!paragraph.list" class="list-decimal mt-1">
                  <li
                    v-for="(l, lidx) in paragraph.list"
                    :key="idx - pidx - lidx"
                    class="text-sm leading-6 text-gray-600"
                    v-text="l"
                  ></li>
                </ol>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <div class="border-t py-12 px-4 sm:py-16 sm:px-6 lg:px-8">
      <h2
        class="text-2xl leading-8 font-extrabold text-gray-900 sm:text-3xl sm:leading-9"
      >
        Contact Us
      </h2>
      <div class="mt-3">
        <p class="text-lg leading-7 text-gray-600">
          In order to resolve a complaint regarding XY Archive or to receive
          further information regarding the use of XY Archive, please contact us
          at:
        </p>
      </div>
      <div class="mt-9">
        <div class="flex">
          <div class="shrink-0">
            <svg
              class="h-6 w-6 text-gray-400"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path
                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
              ></path>
            </svg>
          </div>
          <div class="ml-3 text-base leading-6 text-gray-600">
            <p>XYPN</p>
            <p>24 E Main St</p>
            <p>Bozeman, MT 59715</p>
            <p>United States</p>
            <p>Phone: 406-413-9200</p>
          </div>
        </div>
        <div class="mt-6 flex">
          <div class="shrink-0">
            <svg
              class="h-6 w-6 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              ></path>
            </svg>
          </div>
          <div class="ml-3 text-base leading-6 text-gray-600">
            <p>archive@xyplanningnetwork.com</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
